/**
 * contextualSubline
 *
 * Derives an informational one-liner for the homepage header.
 * Factual and status-oriented — shows where you are in the process,
 * not how you should feel about it.
 */

import type { PlanStep } from "../plan.types";
import type { StepProgress } from "../hooks/usePlanProgress";

type SublineContext = {
  activeStep: PlanStep | null;
  activeProgress: StepProgress | null;
  totalSteps: number;
  completedSteps: number;
  isInTreatment: boolean;
  treatmentDayInPhase?: number;
  treatmentCurrentPhase?: string;
  isComplete: boolean;
  isPartner: boolean;
  primaryName?: string;
};

function getSubstepSummary(progress: StepProgress): {
  total: number;
  complete: number;
  waiting: number;
  nextIncomplete: string | null;
} {
  const total = progress.substeps.length;
  const complete = progress.substeps.filter(
    (s) => s.status === "complete" || s.status === "results-in",
  ).length;
  const waiting = progress.substeps.filter(
    (s) => s.status === "done-awaiting-results" || s.status === "scheduled",
  ).length;
  const next = progress.substeps.find(
    (s) => s.status === "not-started" || s.status === "scheduled",
  );
  return { total, complete, waiting, nextIncomplete: next?.substepId ?? null };
}

/** Human-readable substep names */
const SUBSTEP_LABELS: Record<string, string> = {
  amh: "AMH test",
  "fsh-estradiol": "FSH & Estradiol",
  afc: "Antral follicle count",
  "research-clinics": "clinic research",
  "schedule-consultation": "scheduling a consultation",
  "choose-clinic": "choosing your clinic",
  "medical-history": "medical history",
  "insurance-card": "insurance details",
  "pre-auth": "pre-authorization",
  "consent-forms": "consent forms",
  supplements: "supplements",
  "partner-sa": "partner semen analysis",
  "partner-medical-history": "partner medical history",
  "find-attorney": "finding an attorney",
  "draft-agreement": "drafting the agreement",
  "upload-agreement": "uploading the agreement",
  "pgta-decision": "PGT-A decision",
  "transfer-plan": "transfer plan",
  "find-lab": "finding a lab",
  "get-analysis": "semen analysis",
  "upload-results": "uploading results",
  "schedule-collections": "scheduling collections",
  "collection-1": "first collection",
  "collection-2": "second collection",
  "collection-3": "third collection",
  "confirm-storage": "confirming storage",
  application: "application",
  "medical-screening": "medical screening",
  "psychological-eval": "psych evaluation",
  "genetic-testing": "genetic testing",
  "background-review": "background review",
  "screening-approved": "screening approval",
  "profile-listed": "profile listing",
  matched: "matching",
  "legal-agreement": "legal agreement",
  "cycle-scheduled": "cycle scheduling",
  sa: "semen analysis",
  "medical-exam": "medical exam",
  "freeze-thaw-test": "freeze-thaw test",
};

/** Step-level summary sentences — factual, no fluff */
const STEP_SENTENCES: Record<string, string> = {
  learn: "Overview of the process, timeline, and what to expect.",
  test: "Bloodwork and imaging to establish your baseline.",
  clinic: "Research clinics, book a consultation, and decide.",
  prep: "Medical records, insurance, and consent paperwork.",
  treatment: "Your active treatment cycle.",
  wait: "Waiting period before results are available.",
  legal: "Legal agreements for embryo ownership and storage.",
  screening: "Medical, genetic, and psychological screening.",
  match: "Profile review and matching process.",
  freeze: "Sample collection and long-term storage.",
  donate: "Active donation cycle.",
};

/** Treatment phase labels */
const PHASE_LABELS: Record<string, string> = {
  stimulation: "Stimulation",
  trigger: "Trigger",
  retrieval: "Retrieval",
  recovery: "Recovery",
  transfer: "Transfer",
  tww: "Two-week wait",
  "beta-day": "Beta day",
};

export function getContextualSubline(ctx: SublineContext): string {
  // Partner view
  if (ctx.isPartner) {
    const name = ctx.primaryName ?? "Your partner";
    if (ctx.isInTreatment && ctx.treatmentCurrentPhase) {
      const phase = PHASE_LABELS[ctx.treatmentCurrentPhase] ?? "treatment";
      return `${name} is in ${phase.toLowerCase()}`;
    }
    return `Supporting ${name}`;
  }

  // Cycle complete
  if (ctx.isComplete) {
    return "Cycle complete";
  }

  // Active treatment
  if (ctx.isInTreatment && ctx.treatmentCurrentPhase) {
    const phase = PHASE_LABELS[ctx.treatmentCurrentPhase] ?? "Treatment";
    if (ctx.treatmentDayInPhase) {
      return `${phase} · day ${ctx.treatmentDayInPhase}`;
    }
    return phase;
  }

  // No active step
  if (!ctx.activeStep || !ctx.activeProgress) {
    return `You're on step ${ctx.completedSteps + 1} of ${ctx.totalSteps}.`;
  }

  const stepSentence =
    STEP_SENTENCES[ctx.activeStep.id] ?? ctx.activeStep.subtitle;

  const { total, complete, waiting, nextIncomplete } = getSubstepSummary(
    ctx.activeProgress,
  );

  // If there are substeps, show progress
  if (total > 0) {
    // Waiting on results
    if (waiting > 0) {
      const waitingSub = ctx.activeProgress.substeps.find(
        (s) => s.status === "done-awaiting-results",
      );
      if (waitingSub) {
        const label =
          SUBSTEP_LABELS[waitingSub.substepId] ?? waitingSub.substepId;
        return `Waiting on your ${label} results.`;
      }
      return `${ctx.activeStep.title} — ${complete} of ${total} done.`;
    }

    // In progress with items done
    if (complete > 0 && complete < total) {
      if (nextIncomplete) {
        const label = SUBSTEP_LABELS[nextIncomplete] ?? nextIncomplete;
        return `Next up: ${label}.`;
      }
      return `${ctx.activeStep.title} — ${complete} of ${total} done.`;
    }
  }

  // No substep progress or no substeps — use the step sentence
  return stepSentence;
}
